/**
 * Deal Underwriter — pure math.
 *
 * Two modes:
 *   - For-Sale (Flip): Acquisition + Construction → Sale → Net Proceeds, ROI, IRR
 *   - Rental (Hold):   Acquisition + Construction → Lease → Multi-year proforma + Exit
 *
 * Everything below is pure (no DOM, no fetch). Easy to test.
 */

// ---- Standard finance helpers ----
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const sumValues = (obj) => Object.values(obj || {}).reduce((a, b) => a + (Number(b) || 0), 0);

/** Annual debt service via standard amortization formula. */
export const annualDebtService = (principal, annualRatePct, termYears) => {
  const P = Number(principal) || 0;
  const r = (Number(annualRatePct) || 0) / 100 / 12; // monthly rate
  const n = (Number(termYears) || 0) * 12;
  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / (n / 12);
  const monthly = (P * r) / (1 - Math.pow(1 + r, -n));
  return monthly * 12;
};

/** Newton-Raphson IRR for an array of annual cash flows where index 0 is t=0. */
export const irr = (cashflows, guess = 0.1) => {
  // Sanity: need at least one negative + one positive cashflow
  const hasNeg = cashflows.some((c) => c < 0);
  const hasPos = cashflows.some((c) => c > 0);
  if (!hasNeg || !hasPos) return null;
  const f = (rate) => cashflows.reduce((acc, cf, t) => acc + cf / Math.pow(1 + rate, t), 0);
  const fp = (rate) => cashflows.reduce((acc, cf, t) => acc - (t * cf) / Math.pow(1 + rate, t + 1), 0);
  let r = guess;
  for (let i = 0; i < 100; i++) {
    const v = f(r);
    if (Math.abs(v) < 1e-7) break;
    const d = fp(r);
    if (d === 0) break;
    const next = r - v / d;
    if (!isFinite(next)) break;
    if (Math.abs(next - r) < 1e-9) { r = next; break; }
    r = next;
    if (r < -0.99) r = -0.99; // floor to avoid divide-by-zero
  }
  // Reject implausible results (>1000% IRR almost certainly means non-convergence)
  if (!isFinite(r) || r < -0.99 || r > 10) return null;
  return r;
};

// ====================================================================
// NATIONAL AVERAGE DEFAULTS (sourced from your 408 4th Ave closing doc)
// ====================================================================
export const NATIONAL_AVG_CLOSING = {
  // Selling-side (% of sale price unless noted)
  commission_seller_pct: 2.5,
  commission_buyer_pct: 3.0,
  title_settlement_flat: 480,
  title_abstract_flat: 480,
  admin_compliance_flat: 395,
  state_transfer_tax_pct: 0.16,   // Iowa state stamps ~ $1.60 per $1,000
  recording_flat: 50,
  home_warranty_flat: 697,
  courier_flat: 35,
  buyer_concession_pct: 1.75,     // typical negotiated credit
  // Acquisition closing costs (when buying)
  acquisition_title_pct: 0.5,     // title insurance + escrow
  acquisition_legal_flat: 750,
  acquisition_inspection_flat: 500,
};

// ====================================================================
// BLANK INPUT SHAPES
// ====================================================================
export const blankInputs = (mode = 'for_sale') => ({
  mode,
  units: 1,
  acquisition: {
    purchase_price_home: 0,
    purchase_price_land: 0,
    sqft: 0,
    closing_costs: 0,
    closing_costs_use_avg: true,    // toggle: avg vs entered
    closing_costs_breakdown: {},    // populated when use_avg=false
    insurance: 0,
    property_type: mode === 'rental' ? 'rental' : 'residential',
  },
  construction: {
    // 8 sections that mirror DrawReady's existing construction phases
    categories: {
      site_prep: 0,
      structure: 0,
      mechanicals: 0,
      exterior: 0,
      interior: 0,
      finish: 0,
      options: 0,
      indirect: 0,
    },
    contingency: 0,
    duration_months: 6,
  },
  financing: {
    loan_amount: 0,
    interest_rate: 7.5,    // %
    term_months: 12,       // for the construction loan
    equity: 0,
    interest_only: true,   // most construction loans are IO
  },
  // For-Sale specific
  sale: {
    sale_price: 0,
    months_to_sell: 6,        // post-completion marketing time
    use_avg_closing: true,
    closing_overrides: {},    // line-item overrides
  },
  // Rental specific
  rental: {
    monthly_rent_per_unit: 0,
    annual_rent_growth_pct: 3,
    vacancy_pct: 5,
    other_income: [],   // [{label, monthly_amount}]
    operating_expenses: {
      property_management_pct: 10,        // % of EGI
      cap_repair_fund_pct: 3,             // % of EGI
      property_tax_per_unit_yr: 3896.10,  // matches Ellis Row default
      insurance_yr: 5600,
      accounting_yr: 1100,
      advertising_yr: 500,
      maintenance_yr: 3000,
      utilities_yr: 0,
      other_yr: 0,
    },
    // Long-term loan (perm financing after construction)
    perm_loan_amount: 0,
    perm_interest_rate: 6.875,
    perm_term_years: 25,
    // Exit
    hold_years: 10,
    exit_cap_rate: 6.5,
    selling_costs_pct: 6,
  },
  // Bank underwriting requirements (Jun 2026) — each lender funds differently.
  // Enter your bank's thresholds; the Bank Readiness panel grades the deal.
  bank: {
    name: '',
    target_dscr: 1.10,       // e.g. "my bank wants 1.1 DSCR"
    min_equity_pct: 20,      // borrower must bring at least this % of total cost
    max_ltc_pct: 85,         // max loan-to-cost
    max_ltv_pct: 75,         // max loan-to-value (vs ARV)
    min_profit_margin_pct: 15, // for flips: profit / total cost
  },
});

// ====================================================================
// FOR-SALE / FLIP COMPUTATION
// ====================================================================
export const computeSaleClosingCosts = (salePrice, sale) => {
  const sp = Number(salePrice) || 0;
  if (sale.use_avg_closing) {
    const A = NATIONAL_AVG_CLOSING;
    const lines = {
      commission_seller: sp * (A.commission_seller_pct / 100),
      commission_buyer:  sp * (A.commission_buyer_pct / 100),
      title_settlement:  A.title_settlement_flat,
      title_abstract:    A.title_abstract_flat,
      admin_compliance:  A.admin_compliance_flat,
      state_transfer:    sp * (A.state_transfer_tax_pct / 100),
      recording:         A.recording_flat,
      home_warranty:     A.home_warranty_flat,
      courier:           A.courier_flat,
      buyer_concession:  sp * (A.buyer_concession_pct / 100),
    };
    const total = sumValues(lines);
    return { lines, total: round2(total), source: 'avg' };
  }
  const lines = sale.closing_overrides || {};
  return { lines, total: round2(sumValues(lines)), source: 'actual' };
};

export const computeForSale = (inp) => {
  const acq = inp.acquisition || {};
  const con = inp.construction || {};
  const fin = inp.financing || {};
  const sale = inp.sale || {};

  const acquisitionTotal = (Number(acq.purchase_price_home) || 0) +
                           (Number(acq.purchase_price_land) || 0) +
                           (Number(acq.closing_costs) || 0) +
                           (Number(acq.insurance) || 0);

  const constructionHard = sumValues(con.categories || {});
  const contingency = Number(con.contingency) || 0;
  const constructionTotal = constructionHard + contingency;

  // Construction loan interest (interest-only assumption for project duration + months_to_sell)
  const months = (Number(con.duration_months) || 0) + (Number(sale.months_to_sell) || 0);
  const ratePctMonthly = (Number(fin.interest_rate) || 0) / 100 / 12;
  // Approximate average outstanding balance ≈ 50% of loan during build, full during marketing
  const avgBuildBal = (Number(fin.loan_amount) || 0) * 0.5;
  const fullBal = Number(fin.loan_amount) || 0;
  const buildMonths = Number(con.duration_months) || 0;
  const sellMonths = Number(sale.months_to_sell) || 0;
  const carryInterest = (avgBuildBal * ratePctMonthly * buildMonths) +
                        (fullBal * ratePctMonthly * sellMonths);

  const totalProjectCost = acquisitionTotal + constructionTotal + carryInterest;

  const closing = computeSaleClosingCosts(sale.sale_price, sale);
  const netSaleProceeds = (Number(sale.sale_price) || 0) - closing.total;

  const netProfit = netSaleProceeds - totalProjectCost;
  const equity = Number(fin.equity) || (totalProjectCost - (Number(fin.loan_amount) || 0));
  const roi = equity > 0 ? (netProfit / equity) * 100 : 0;

  // Annualized return based on total months (build + sell)
  const yrs = months > 0 ? months / 12 : 1;
  const annualizedRoi = equity > 0 && netProfit + equity > 0
    ? (Math.pow((netProfit + equity) / equity, 1 / yrs) - 1) * 100
    : 0;

  // Simple IRR: month 0 outflow = equity, month N inflow = equity + netProfit
  // Skip when equity ≤ 0 to avoid divergent / nonsensical IRR
  let irrVal = null;
  if (equity > 0 && months > 0) {
    const periodsYr = Math.max(1, Math.ceil(months / 12));
    const cashflows = Array(periodsYr + 1).fill(0);
    cashflows[0] = -equity;
    cashflows[periodsYr] = equity + netProfit;
    irrVal = irr(cashflows);
  }

  return {
    acquisitionTotal: round2(acquisitionTotal),
    constructionHard: round2(constructionHard),
    contingency: round2(contingency),
    constructionTotal: round2(constructionTotal),
    carryInterest: round2(carryInterest),
    totalProjectCost: round2(totalProjectCost),
    salePrice: round2(Number(sale.sale_price) || 0),
    closing,
    netSaleProceeds: round2(netSaleProceeds),
    netProfit: round2(netProfit),
    equity: round2(equity),
    roi: round2(roi),
    annualizedRoi: round2(annualizedRoi),
    irr: irrVal == null ? null : round2(irrVal * 100),
    holdMonths: months,
  };
};

// ====================================================================
// RENTAL / HOLD COMPUTATION (matches Ellis Row pro forma format)
// ====================================================================
export const computeRental = (inp) => {
  const acq = inp.acquisition || {};
  const con = inp.construction || {};
  const fin = inp.financing || {};
  const r = inp.rental || {};
  const units = Number(inp.units) || 1;

  const acquisitionTotal = (Number(acq.purchase_price_home) || 0) +
                           (Number(acq.purchase_price_land) || 0) +
                           (Number(acq.closing_costs) || 0) +
                           (Number(acq.insurance) || 0);
  const constructionHard = sumValues(con.categories || {});
  const contingency = Number(con.contingency) || 0;
  const constructionTotal = constructionHard + contingency;
  const totalDevCost = acquisitionTotal + constructionTotal;

  const holdYrs = Math.max(1, Math.min(30, Number(r.hold_years) || 10));
  const rentGrowth = (Number(r.annual_rent_growth_pct) || 0) / 100;
  const vacancy = (Number(r.vacancy_pct) || 0) / 100;
  const baseRent = Number(r.monthly_rent_per_unit) || 0;
  const otherIncome = (r.other_income || []).reduce(
    (a, line) => a + (Number(line.monthly_amount) || 0) * 12, 0);

  const annualPermDebt = annualDebtService(
    r.perm_loan_amount,
    r.perm_interest_rate,
    r.perm_term_years
  );

  const opex = r.operating_expenses || {};
  const propTax = (Number(opex.property_tax_per_unit_yr) || 0) * units;
  const fixedOpex =
    (Number(opex.insurance_yr) || 0) +
    (Number(opex.accounting_yr) || 0) +
    (Number(opex.advertising_yr) || 0) +
    (Number(opex.maintenance_yr) || 0) +
    (Number(opex.utilities_yr) || 0) +
    (Number(opex.other_yr) || 0) +
    propTax;

  const years = [];
  for (let y = 1; y <= holdYrs; y++) {
    const monthlyRent = baseRent * Math.pow(1 + rentGrowth, y - 1);
    const gpr = monthlyRent * 12 * units + otherIncome;
    const vacancyAllowance = gpr * vacancy;
    const egi = gpr - vacancyAllowance;
    const propMgmt = egi * ((Number(opex.property_management_pct) || 0) / 100);
    const capRepair = egi * ((Number(opex.cap_repair_fund_pct) || 0) / 100);
    const totalOpex = propMgmt + capRepair + fixedOpex;
    const noi = egi - totalOpex;
    const cashFlow = noi - annualPermDebt;
    const dscr = annualPermDebt > 0 ? noi / annualPermDebt : null;
    years.push({
      year: y,
      monthly_rent: round2(monthlyRent),
      gpr: round2(gpr),
      vacancy: round2(vacancyAllowance),
      egi: round2(egi),
      prop_mgmt: round2(propMgmt),
      cap_repair: round2(capRepair),
      fixed_opex: round2(fixedOpex),
      total_opex: round2(totalOpex),
      noi: round2(noi),
      debt_service: round2(annualPermDebt),
      cash_flow: round2(cashFlow),
      dscr: dscr == null ? null : round2(dscr),
    });
  }

  // Exit: sell at end of holdYrs at exit cap rate against year (holdYrs+1) NOI
  const finalYearNOI = years[years.length - 1].noi;
  const nextYearNOI = finalYearNOI * (1 + rentGrowth); // rough proxy
  const exitCap = (Number(r.exit_cap_rate) || 0) / 100;
  const exitValue = exitCap > 0 ? nextYearNOI / exitCap : 0;
  const sellingCosts = exitValue * ((Number(r.selling_costs_pct) || 0) / 100);
  const loanBalanceApprox = Number(r.perm_loan_amount) || 0; // simplified: ignore amortization
  const netExitProceeds = exitValue - sellingCosts - loanBalanceApprox;

  // Build IRR cashflows
  const equity = Number(fin.equity) || (totalDevCost - (Number(fin.loan_amount) || 0));
  const cf = [-equity];
  years.forEach((yr, idx) => {
    if (idx === years.length - 1) {
      cf.push(yr.cash_flow + netExitProceeds);
    } else {
      cf.push(yr.cash_flow);
    }
  });
  const irrVal = irr(cf);
  const totalCashFlow = years.reduce((a, y) => a + y.cash_flow, 0);
  const equityMultiple = equity > 0 ? (totalCashFlow + netExitProceeds + equity) / equity : 0;
  const avgCoC = years.length > 0 && equity > 0
    ? (totalCashFlow / years.length / equity) * 100
    : 0;

  return {
    acquisitionTotal: round2(acquisitionTotal),
    constructionHard: round2(constructionHard),
    contingency: round2(contingency),
    constructionTotal: round2(constructionTotal),
    totalDevCost: round2(totalDevCost),
    annualPermDebt: round2(annualPermDebt),
    years,
    exitValue: round2(exitValue),
    sellingCosts: round2(sellingCosts),
    loanBalanceApprox: round2(loanBalanceApprox),
    netExitProceeds: round2(netExitProceeds),
    equity: round2(equity),
    irr: irrVal == null ? null : round2(irrVal * 100),
    equityMultiple: round2(equityMultiple),
    avgCashOnCash: round2(avgCoC),
    year1DSCR: years[0]?.dscr ?? null,
    year1CashFlow: years[0]?.cash_flow ?? 0,
  };
};

// ====================================================================
// CONSTRUCTION CATEGORIES (mirror DrawReady's 8-phase model)
// ====================================================================
export const CONSTRUCTION_CATEGORIES = [
  { key: 'site_prep',    label: 'Site Prep',                 hint: 'Excavation · Foundation · Walls · Slab' },
  { key: 'structure',    label: 'Structure',                 hint: 'Framing · Floor deck · Roofing · Windows' },
  { key: 'mechanicals',  label: 'Mechanicals',               hint: 'Electrical · HVAC · Plumbing' },
  { key: 'exterior',     label: 'Exterior',                  hint: 'Gutters · Insulation · Siding · Landscaping' },
  { key: 'interior',     label: 'Interior',                  hint: 'Cabinets · Drywall · Flooring' },
  { key: 'finish',       label: 'Finish',                    hint: 'Fixtures · Paint · Trim · Doors' },
  { key: 'options',      label: 'Options',                   hint: 'Appliances · Fireplace · Upgrades' },
  { key: 'indirect',     label: 'Indirect',                  hint: 'Permits · Cleaning · Professional fees' },
];

export const CLOSING_LINE_LABELS = {
  commission_seller: "Seller's Agent Commission",
  commission_buyer:  "Buyer's Agent Commission",
  title_settlement:  'Title Settlement Fee',
  title_abstract:    'Title Abstract Fee',
  admin_compliance:  'Admin / Compliance Fee',
  state_transfer:    'State Transfer Tax',
  recording:         'Recording Fees',
  home_warranty:     'Home Warranty',
  courier:           'Courier / Wire Fee',
  buyer_concession:  'Buyer Concession (typical)',
};


// ====================================================================
// BANK READINESS (Jun 2026) — grade a deal against a lender's rules.
// Every bank funds differently, so thresholds come from inputs.bank.
// ====================================================================
export const computeBankReadiness = (inp, result, mode) => {
  const bank = inp.bank || {};
  const fin = inp.financing || {};
  const loanAmount = Number(fin.loan_amount) || 0;

  const totalCost = mode === 'for_sale'
    ? (result.totalProjectCost || 0)
    : (result.totalDevCost || 0);
  const arv = mode === 'for_sale'
    ? (result.salePrice || 0)
    : (result.exitValue || 0);
  const equity = result.equity || 0;

  const ltc = totalCost > 0 ? (loanAmount / totalCost) * 100 : null;
  const ltv = arv > 0 ? (loanAmount / arv) * 100 : null;
  const equityPct = totalCost > 0 ? (equity / totalCost) * 100 : null;
  const dscr = mode === 'rental' ? (result.year1DSCR ?? null) : null;
  const profitMarginPct = mode === 'for_sale' && totalCost > 0
    ? ((result.netProfit || 0) / totalCost) * 100
    : null;

  const metrics = [];

  // LTC — lower is safer, must be ≤ max
  metrics.push({
    key: 'ltc',
    label: 'Loan-to-Cost (LTC)',
    value: ltc,
    target: Number(bank.max_ltc_pct) || 0,
    ok: ltc == null ? null : ltc <= (Number(bank.max_ltc_pct) || 0),
    unit: '%',
    rule: `≤ ${Number(bank.max_ltc_pct) || 0}%`,
    hint: 'Loan ÷ total project cost',
  });
  // LTV — lower is safer, must be ≤ max
  metrics.push({
    key: 'ltv',
    label: 'Loan-to-Value (LTV / ARV)',
    value: ltv,
    target: Number(bank.max_ltv_pct) || 0,
    ok: ltv == null ? null : ltv <= (Number(bank.max_ltv_pct) || 0),
    unit: '%',
    rule: `≤ ${Number(bank.max_ltv_pct) || 0}%`,
    hint: 'Loan ÷ after-repair value',
  });
  // Borrower equity — higher is safer, must be ≥ min
  metrics.push({
    key: 'equity',
    label: 'Borrower Equity',
    value: equityPct,
    target: Number(bank.min_equity_pct) || 0,
    ok: equityPct == null ? null : equityPct >= (Number(bank.min_equity_pct) || 0),
    unit: '%',
    rule: `≥ ${Number(bank.min_equity_pct) || 0}%`,
    hint: 'Cash the borrower brings, as % of cost',
  });

  if (mode === 'rental') {
    metrics.push({
      key: 'dscr',
      label: 'DSCR (Year 1)',
      value: dscr,
      target: Number(bank.target_dscr) || 0,
      ok: dscr == null ? null : dscr >= (Number(bank.target_dscr) || 0),
      unit: 'x',
      rule: `≥ ${(Number(bank.target_dscr) || 0).toFixed(2)}x`,
      hint: 'Net operating income ÷ debt service',
    });
  } else {
    metrics.push({
      key: 'profit',
      label: 'Profit Margin',
      value: profitMarginPct,
      target: Number(bank.min_profit_margin_pct) || 0,
      ok: profitMarginPct == null ? null : profitMarginPct >= (Number(bank.min_profit_margin_pct) || 0),
      unit: '%',
      rule: `≥ ${Number(bank.min_profit_margin_pct) || 0}%`,
      hint: 'Net profit ÷ total project cost',
    });
  }

  const graded = metrics.filter((m) => m.ok !== null);
  const passing = graded.filter((m) => m.ok).length;
  const failing = graded.filter((m) => !m.ok);
  const allPass = graded.length > 0 && failing.length === 0;

  return {
    verdict: graded.length === 0 ? 'incomplete' : (allPass ? 'ready' : 'needs_work'),
    passing,
    total: graded.length,
    metrics,
    failing: failing.map((m) => m.key),
    cashToClose: equity,
  };
};
